import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * AuthorBio — renders a visible author attribution block with rich JSON-LD schema.
 * Place this near the top of any content-heavy page to signal E-E-A-T expertise to
 * Google, Bing, ChatGPT, and Perplexity crawlers.
 *
 * Props:
 *  compact  — boolean, renders a slim inline version for blog posts / service pages
 */
export default function AuthorBio({ compact = false }) {
  useEffect(() => {
    // Inject per-page author Person schema (de-duped by attribute)
    if (!document.querySelector('script[data-author-bio-schema]')) {
      const schema = {
        "@context": "https://schema.org",
        "@type": "Person",
        "@id": "https://trinitydentalarts.com/meet-the-doctor#author",
        "name": "Dr. Xhoana Gjelaj",
        "honorificPrefix": "Dr.",
        "honorificSuffix": "DMD, MAGD, DABOI/ID, DICOI, FAAID",
        "jobTitle": "Board-Certified Implantologist & Lead Dentist",
        "description": "Dr. Xhoana Gjelaj, DMD, practices implant and restorative dentistry in Trinity, FL. Her professional credentials include MAGD Mastership, DABOI/ID and DICOI diplomate designations, and FAAID Fellowship.",
        "url": "https://trinitydentalarts.com/meet-the-doctor",
        "image": "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/697e88817b05b4051a928e3f/fb403896a_IMG_0190.jpg",
        "sameAs": [
        "https://aaid-implant.org/dentist/gjelaj-xhoana/",
        "https://agd.org/practice/tools/patient-resources/find-an-agd-dentist/PracticeInfo/?id=0csy%2biH76jQ%3d",
        "https://www.healthgrades.com/dentist/dr-xhoana-gjelaj-xbk4c"],

        "alumniOf": [
        { "@type": "CollegeOrUniversity", "name": "Nova Southeastern University College of Dental Medicine", "sameAs": "https://www.nova.edu/" },
        { "@type": "EducationalOrganization", "name": "Dawson Academy", "sameAs": "https://www.thedawsonacademy.com/" },
        { "@type": "EducationalOrganization", "name": "Pankey Institute", "sameAs": "https://www.pankey.org/" }],

        "hasCredential": [
        {
          "@type": "EducationalOccupationalCredential",
          "name": "Doctor of Dental Medicine (DMD)",
          "credentialCategory": "Degree",
          "educationalLevel": "Doctorate",
          "recognizedBy": { "@type": "CollegeOrUniversity", "name": "Nova Southeastern University" }
        },
        {
          "@type": "EducationalOccupationalCredential",
          "name": "Mastership, Academy of General Dentistry (MAGD)",
          "credentialCategory": "Mastership",
          "recognizedBy": { "@type": "Organization", "name": "Academy of General Dentistry", "sameAs": "https://agd.org" }
        },
        {
          "@type": "EducationalOccupationalCredential",
          "name": "Diplomate, American Board of Oral Implantology / Implant Dentistry (DABOI/ID)",
          "credentialCategory": "BoardCertification",
          "recognizedBy": { "@type": "Organization", "name": "American Board of Oral Implantology", "sameAs": "https://www.aboi.org" }
        },
        {
          "@type": "EducationalOccupationalCredential",
          "name": "Diplomate, International Congress of Oral Implantologists (DICOI)",
          "credentialCategory": "Diplomate",
          "recognizedBy": { "@type": "Organization", "name": "International Congress of Oral Implantologists", "sameAs": "https://icoi.world" }
        },
        {
          "@type": "EducationalOccupationalCredential",
          "name": "Fellow, American Academy of Implant Dentistry (FAAID)",
          "credentialCategory": "Fellowship",
          "recognizedBy": { "@type": "Organization", "name": "American Academy of Implant Dentistry", "sameAs": "https://aaid-implant.org" }
        }],

        "memberOf": [
        { "@type": "Organization", "name": "American Academy of Implant Dentistry", "sameAs": "https://aaid-implant.org" },
        { "@type": "Organization", "name": "American Board of Oral Implantology", "sameAs": "https://www.aboi.org" },
        { "@type": "Organization", "name": "Academy of General Dentistry", "sameAs": "https://agd.org" },
        { "@type": "Organization", "name": "International Congress of Oral Implantologists", "sameAs": "https://icoi.world" },
        { "@type": "Organization", "name": "American Dental Association", "sameAs": "https://www.ada.org" }],

        "award": [
        "MAGD Mastership — Academy of General Dentistry",
        "DABOI/ID Board Certification — Implant Dentistry",
        "DICOI Diplomate",
        "FAAID Fellowship"],

        "knowsAbout": [
        "Dental Implants", "All-on-4", "All-on-6", "Full Mouth Reconstruction",
        "Bone Grafting", "Sinus Lifts", "Cosmetic Dentistry", "Porcelain Veneers",
        "Smile Makeovers", "Invisalign", "Sedation Dentistry", "Laser Dentistry",
        "Regenerative Dentistry", "CEREC Same-Day Crowns", "Emergency Dentistry"],

        "worksFor": {
          "@type": "Dentist",
          "name": "Trinity Dental Arts",
          "url": "https://trinitydentalarts.com"
        }
      };
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-author-bio-schema', 'true');
      s.text = JSON.stringify(schema);
      document.head.appendChild(s);
    }
    return () => {
      const s = document.querySelector('script[data-author-bio-schema]');
      if (s) s.remove();
    };
  }, []);

  if (compact) {
    return (
      <div className="flex items-center gap-3 py-3 border-t border-b border-slate-100 my-6" itemScope itemType="https://schema.org/Person">
        <img
          src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/697e88817b05b4051a928e3f/fb403896a_IMG_0190.jpg"
          alt="Dr. Xhoana Gjelaj"
          className="w-10 h-10 rounded-full object-cover object-top flex-shrink-0"
          itemProp="image" />
        
        <div>
          <p className="text-sm font-medium text-slate-800">
            <span itemProp="name">Dr. Xhoana Gjelaj</span>
            <span className="text-amber-600 ml-1" itemProp="honorificSuffix">DMD, MAGD, DABOI/ID, DICOI, FAAID</span>
          </p>
          <p className="text-xs text-slate-500" itemProp="description">
            Board-Certified Implantologist · MAGD Mastership ·{' '}
            <Link to="/meet-the-doctor" className="text-amber-600 hover:underline">View full credentials →</Link>
          </p>
        </div>
      </div>);

  }

  return (
    <aside
      className="bg-amber-50 border border-amber-200 rounded-2xl p-6 my-8"
      aria-label="About the author"
      itemScope
      itemType="https://schema.org/Person">
      
      <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-4">About the Author</p>
      <div className="flex gap-4 items-start">
        <img
          src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/697e88817b05b4051a928e3f/fb403896a_IMG_0190.jpg"
          alt="Dr. Xhoana Gjelaj, DMD, MAGD, DABOI/ID, DICOI, FAAID"
          className="w-20 h-20 rounded-xl object-cover object-top flex-shrink-0"
          itemProp="image" />
        
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-0.5" itemProp="name">Dr. Xhoana Gjelaj</h3>
          <p className="text-sm font-medium text-amber-700 mb-2" itemProp="honorificSuffix">DMD, MAGD, DABOI/ID, DICOI, FAAID</p>
          <p className="text-sm text-slate-600 leading-relaxed mb-3" itemProp="description">Dr. Gjelaj practices implant and restorative dentistry in Trinity, FL. Her professional credentials include MAGD Mastership, DABOI/ID and DICOI diplomate designations, and FAAID Fellowship. The links below provide more information about the designations. 




          </p>
          <div className="flex flex-wrap gap-2">
            {[
            { label: 'AAID Fellowship and ABOI/ID Diplomate', href: 'https://aaid-implant.org/dentist/gjelaj-xhoana/' },
            { label: 'About the ICOI Diplomate designation', href: 'https://www.icoi.org/icoi-diplomate/' },
            { label: 'AGD Mastership', href: 'https://agd.org/practice/tools/patient-resources/find-an-agd-dentist/PracticeInfo/?id=0csy%2biH76jQ%3d' },
            { label: 'Full Bio', href: '/meet-the-doctor', internal: true }].
            map(({ label, href, internal }) =>
            internal ?
            <Link key={label} to={href} className="text-xs px-3 py-1 bg-white border border-amber-300 text-amber-700 rounded-full hover:bg-amber-100 transition-colors">
                  {label}
                </Link> :

            <a key={label} href={href} target="_blank" rel="noopener noreferrer"
            className="text-xs px-3 py-1 bg-white border border-amber-300 text-amber-700 rounded-full hover:bg-amber-100 transition-colors">
                  {label}
                </a>

            )}
          </div>
        </div>
      </div>
    </aside>);

}